import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { OptimizedImage } from '@/components/OptimizedImage';
import { Car, Bus, Train, Plane, Users, Home, Ticket } from 'lucide-react';
import { getBlurPlaceholder } from '@/utils/blurPlaceholders';

const LocationAccessSection: React.FC = () => {
    const { t } = useLanguage();

    // Transportation options for right column
    const transportOptions = [
        {
            id: 'air',
            icon: Plane,
            details: t('activities.locationAccess.air.details'),
        },
        {
            id: 'bus',
            icon: Bus,
            details: t('activities.locationAccess.bus.description'),
        },
        {
            id: 'train',
            icon: Train,
            details: t('activities.locationAccess.train.description'),
        },
        {
            id: 'parking',
            icon: Car,
            details: t('activities.locationAccess.parking.description'),
        },
    ];

    return (
        <section className="bg-elegant-white py-20 md:py-24 px-4 md:px-6 lg:px-0">
            <div className="max-w-7xl mx-auto">
                {/* Split Layout: Map/Image Left, Content Right */}
                <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] gap-8 lg:gap-12 items-center">
                    {/* Left Column - Map/Image */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.6 }}
                        className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-[750px] rounded-2xl lg:rounded-none lg:rounded-l-2xl overflow-hidden shadow-2xl"
                    >
                        {/* Google Maps Embed */}
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2736.408682929248!2d7.7945598!3d46.6976705!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478fa541a569a32d%3A0x786359f5bdf1a5e4!2sBeatenberg%20Resort%20%26%20Spa!5e0!3m2!1sen!2sin!4v1760786128427!5m2!1sen!2sin"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="absolute inset-0"
                            title={t('activities.locationAccess.mapTitle')}
                        />
                        {/* Subtle overlay for depth */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" />
                    </motion.div>

                    {/* Right Column - Content */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="space-y-8 px-4 md:px-6 lg:px-8 lg:pl-12"
                    >
                        {/* Section Header */}
                        <div className="space-y-4">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5 }}
                            >
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-charcoal-gray mb-4 leading-tight">
                                    {t('activities.locationAccess.title')}
                                </h2>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <p className="text-lg md:text-xl text-charcoal-gray/80 font-lato leading-relaxed max-w-2xl">
                                    {t('activities.locationAccess.subtitle')}
                                </p>
                            </motion.div>
                        </div>

                        {/* Access Info - Exact Table Format from "At a Glance" */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="mt-8 bg-white border-2 border-dotted border-charcoal-gray/20 rounded-lg overflow-hidden"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 divide-x-0 md:divide-x-2 divide-dotted divide-charcoal-gray/20">
                                {/* Left Column - Logo & Location */}
                                <div className="p-4 md:p-6 flex flex-col items-center justify-center">
                                    <div className="w-full max-w-[200px] mb-4">
                                        <OptimizedImage
                                            src="/BRS-Full-logo.webp"
                                            srcSet="/BRS-Full-logo.webp 400w, /BRS-Full-logo@2x.webp 800w"
                                            sizes="(max-width: 768px) 200px, 400px"
                                            alt="Beatenberg Resort & Spa Logo"
                                            loading="lazy"
                                            decoding="async"
                                            width={400}
                                            height={400}
                                            className="w-full h-auto object-contain"
                                            blurDataURL={getBlurPlaceholder("/BRS-Full-logo.webp")}
                                            fetchPriority="auto"
                                        />
                                    </div>
                                    <p className="text-sm md:text-base text-charcoal-gray/80 font-lato text-center">
                                        {t('activities.locationAccess.locatedAt')}
                                    </p>
                                </div>

                                {/* Right Column - Transportation Options */}
                                <div className="divide-y-2 divide-dotted divide-charcoal-gray/20">
                                    {transportOptions.map((option, index) => {
                                        const IconComponent = option.icon;
                                        return (
                                            <motion.div
                                                key={option.id}
                                                initial={{ opacity: 0, x: 10 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                                                className="p-4 md:p-6"
                                            >
                                                <div className="grid grid-cols-[auto_1fr] gap-4 items-center">
                                                    {/* Icon Column */}
                                                    <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                                                        <IconComponent className="w-6 h-6 text-charcoal-gray" strokeWidth={2} />
                                                    </div>

                                                    {/* Details Column */}
                                                    <div className="min-w-0">
                                                        {option.id === 'air' ? (
                                                            <div className="space-y-1 text-sm text-charcoal-gray/80 font-lato">
                                                                <p>{t('activities.locationAccess.air.bern')}</p>
                                                                <p>{t('activities.locationAccess.air.zurich')}</p>
                                                                <p>{t('activities.locationAccess.air.geneva')}</p>
                                                            </div>
                                                        ) : option.id === 'train' ? (
                                                            <div className="space-y-1 text-sm text-charcoal-gray/80 font-lato">
                                                                <p>{t('activities.locationAccess.train.beatenberg')}</p>
                                                                <p>{t('activities.locationAccess.train.interlaken')}</p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-charcoal-gray/80 font-lato">
                                                                {option.details}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        </motion.div>

                        {/* Benefits Section - Inside Text Column */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            className="mt-12"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                                {t("activities.footer.benefits").map((benefit: string, idx: number) => {
                                    // Define icons for each benefit
                                    const icons = [Users, Home, Ticket];
                                    const IconComponent = icons[idx];
                                    
                                    return (
                                        <div key={idx} className="flex flex-col items-center text-center space-y-4">
                                            <div className="w-12 h-12 rounded-full border border-alpine-gold/30 flex items-center justify-center text-alpine-gold bg-alpine-gold/5">
                                                <IconComponent className="w-6 h-6 text-alpine-gold" strokeWidth={2} />
                                            </div>
                                            <h3 className="text-charcoal-gray font-bold tracking-[0.1em] uppercase text-xs">
                                                {benefit}
                                            </h3>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-12 text-center">
                                <p className="font-playfair italic text-2xl md:text-3xl text-alpine-gold/60 leading-relaxed max-w-2xl mx-auto">
                                    "{t("activities.footer.tagline")}"
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default LocationAccessSection;

